/**
 * Created by Carsten Hibbeler on 14.02.2016.
 */

import { AppContext, debounce, RemoteDataActions } from 'plugin/liferay-widgets-react-web';
import { featureUtils } from 'plugin/map-portlet';
import ModalActions from '../actions/ModalActions';
import L from 'external/leaflet';

export default class BaseRemoteVectorLayerController {

    /**
     * Use this to mutate layer config used for layer creation.
     *
     * @param {object} layerConfig - options
     * @return {object} layerConfig - options
     */
    layerConfig(layerConfig) {
        return {
            ...layerConfig,
            onClick: this.onFeatureClick.bind(this),
            onMouseOver: this.onMouseOver.bind(this),
        };
    }

    /**
     * Invoked once after layer init.
     *
     * @param {Map} map - leaflet map
     * @param {object} o - global options for the map
     * @param {Layer} layer - leaflet Layer
     * @param {object} layerConfig - config of the layer
     */
    init(map, o, layer, layerConfig, context) {
        this.map = map;
        this.layer = layer || (layer && layer.getInternalLayer && layer.getInternalLayer());
        this.context = context;
        this.lastSelected = [];
        this.originalSelected = o.selectedItems || [];
        this.disabledItems = o.disabledItems || [];
        this.lastSelected = [];

        this.forceUpdate = debounce(this.forceUpdate.bind(this), 300);

        if (layerConfig.className && layerConfig.api) {
            this.className = layerConfig.className;
            this.DATA_NAME = `VectorLayerData_${layerConfig.className}`;
            const webApi = AppContext.getWebApis()[layerConfig.api];
            const allStores = AppContext.getStores();
            const store = allStores.find(s => s.className === layerConfig.className);
            this.removeListener = store && store.addListener(() => {
                console.info('STORE UPDATE', layerConfig.filter.type);
                this.forceUpdate();
            });

            RemoteDataActions.list(this.DATA_NAME, layerConfig.className, {
                start: 0,
                end: AppContext.DEFAULT_LIST_SIZE
            }, webApi.list);
        }
    }

    forceUpdate() {
        const layer = this.getLayer();
        layer.redraw();
    }

    /**
     * Config phase.
     * Use this to mutate options pre <code>update</code>.
     * Do not use the options for third party component updates.
     *
     * @param {object} o - options
     * @return {object} o - options
     */
    options(o) {
        const layer = this.getLayer(),
            { selectedItems } = o;

        if (selectedItems) {
            let selectedLayers = o.selectedLayers || [];

            layer.eachLayer && layer.eachLayer(featureLayer => {
                const { feature } = featureLayer;
                if (this.isSelected(selectedItems, feature)) {
                    selectedLayers = [ ...selectedLayers, featureLayer.original || featureLayer ];
                }

            });

            if (selectedLayers.length > 0) {
                o.selectedLayers = selectedLayers;
            }
        }

        return o;
    }

    /**
     * Update phase.
     * Use this to update based on the global map options.
     * Not invoked after first init.
     *
     * @param {Map} map - leaflet map
     * @param {object} o - options
     */
    update(map, o) {
        const layer = this.getLayer();
        const { selectedItems = [], disabledItems = [] } = o;
        this.lastSelected.forEach(s => layer.resetFeatureStyle(s.className + '_' + s.id));

        disabledItems.forEach(s => {
            layer.setFeatureStyle(s.className + '_' + s.id, {
                weight: 3,
                fillColor: '#b3b3b3',
                color: '#b3b3b3',
                fillOpacity: 0.5,
                fill: true
            });
        });

        selectedItems && selectedItems.forEach(s => layer.setFeatureStyle(s.className + '_' + s.id, {
            weight: 3,
            fillColor: '#FF5733',
            color: '#FF5733',
            fillOpacity: 0.5,
            fill: true
        }));

        this.disabledItems = disabledItems || [];
        this.lastSelected = selectedItems || [];
    }

    updateDeselected(map, layer, featureLayer, o) {
        const { originalLayer } = featureLayer;
        delete featureLayer.originalLayer;
        map.removeLayer(featureLayer);
        originalLayer.addLayer(featureLayer);
        this.lastSelected = this.lastSelected.filter(ls => ls.feature.properties.id !== featureLayer.feature.properties.id);
    }

    updateSelected(map, layer, featureLayer, o) {
        layer.removeLayer(featureLayer);
        if (featureLayer.original) {
            layer.removeLayer(featureLayer.original);
            featureLayer = featureLayer.original;
        }
        featureLayer.originalLayer = layer;
        map.addLayer(featureLayer);
        this.lastSelected = [ ...this.lastSelected, featureLayer ];
    }

    remove(map) {
        if (this.lastSelected) {
            this.lastSelected.forEach(layer => {
                if (layer instanceof L.Layer) {
                    map.removeLayer(layer);
                }
            });
            this.lastSelected = [];
        }
        if (this.removeListener) {
            this.removeListener.remove();
            RemoteDataActions.remove(this.DATA_NAME, this.className);
        }
    }

    getLayer() {
        if (this.layer.getInternalLayer && this.layer.getInternalLayer()) {
            this.layer = this.layer.getInternalLayer();
        }
        return this.layer;
    }

    getProps(map, feature, featureLayer, selectedItems) {
        const props = {};

        if (featureUtils.notMarker(featureLayer)) {

            if (this.isCollapsed(map, featureLayer)) {
                props.renderAsMarker = true;
                featureLayer.marker = featureLayer.marker || this.markerForPolygon(feature, featureLayer);
            }
        } else {
            if (featureLayer.original && this.isCollapsed(map, featureLayer.original)) {
                props.renderAsMarker = true;
            }
        }

        props.selected = this.isSelected(selectedItems, feature);

        return props;
    }

    isSelected(selectedItems, feature) {
        const { id: featureId, type: featureType } = feature.properties;
        return !!(selectedItems && selectedItems.findIndex(item => {
            return (item.item || item).id === featureId && item.className === featureType;
        }) >= 0);
    }

    /**
     * Check if a path is displayed smaller than minSize (in pixels)
     * @param map
     * @param path
     * @param minSize
     * @returns {boolean}
     */
    isCollapsed(map, path, minSize = 50) {
        const zoom = map.getZoom();
        const bounds = path.getBounds();

        const ne_px = map.project(bounds.getNorthEast(), zoom);
        const sw_px = map.project(bounds.getSouthWest(), zoom);

        const width = ne_px.x - sw_px.x;
        const height = sw_px.y - ne_px.y;
        return (height < minSize || width < minSize);
    }

    onMouseOver(e) {
        L.popup()
            .setContent(e.layer.properties.name || e.layer.properties.type)
            .setLatLng(e.latlng);
        //.openOn(map);

        L.DomEvent.stop(e);
    }

    onFeatureClick(e) {
        //e.originalEvent.preventDefault();
        const { layer } = e;
        const allowClick = this.originalSelected.find(item => parseInt(item.id) === parseInt(layer.properties.id)) || !this.disabledItems.find(item => parseInt(item.id) === parseInt(layer.properties.id));
        if (allowClick) {
            this.onItemClick(layer.properties.type, layer.properties.id, layer.properties.title);
        }
    }

    onItemClick(className, id) {
        RemoteDataActions.pageSelected(className, { id });
        ModalActions.showModal(className, { id }, false, undefined, { contextId: this.context && this.context.contextId });
    }
}








// WEBPACK FOOTER //
// ./js/map/BaseRemoteVectorLayerController.js